import { useState } from 'react';

import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoadingButton from '@mui/lab/LoadingButton';
import InputAdornment from '@mui/material/InputAdornment';

import RHFCheckbox from '@webclient/components/react-hook-form/RHFCheckbox';
import RHFInputField from '@webclient/components/react-hook-form/RHFInputField';
import useUserAuth from '@webclient/components/user-authentication/hooks/useUserAuth';
import { setupPasswordSchema } from '@webclient/components/user-authentication/schemas';
import BaseStep from '@webclient/components/user-authentication/sections/steps/components/base-step';

import { yupResolver } from '@hookform/resolvers/yup';
import { IconButton } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import { useForm } from 'react-hook-form';

const defaultValues = {
  password: '',
  is_subscribe_marketing: false,
};

type SetupPasswordFormType = {
  password: string;
  is_subscribe_marketing?: boolean;
};

export default function SetupPassword() {
  const { t } = useTranslate();

  const { handlePassword, handleIsUserSubscribe, handleNextStep } =
    useUserAuth();

  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm({
    resolver: yupResolver<SetupPasswordFormType>(setupPasswordSchema(t)),
    defaultValues,
  });

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
  } = methods;

  function handleNextStepOnClick(data: SetupPasswordFormType) {
    handlePassword(data.password);
    handleIsUserSubscribe(data.is_subscribe_marketing || false);
    handleNextStep();
  }

  return (
    <BaseStep
      slots={{
        header: {
          withBackButton: true,
          heading: t(
            'layout.auth.steps.setupPassword.label',
            'Welcome to Knowz',
          ),
          description: t(
            'layout.auth.steps.setupPassword.description',
            'Create a password which is hard to guess:',
          ),
        },

        form: {
          methods: methods,
          onSubmit: handleSubmit(handleNextStepOnClick),
        },
      }}
    >
      <RHFInputField
        fullWidth
        name="password"
        type={showPassword ? 'text' : 'password'}
        error={!!errors.password}
        autoComplete="new-password" //? https://www.chromium.org/developers/design-documents/form-styles-that-chromium-understands/
        placeholder={t(
          'layout.auth.steps.setupPassword.form.password.placeholder',
          'Create a password',
        )}
        helperText={errors.password?.message}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              variant="ghost"
              color="secondary"
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
            >
              {showPassword ? <RemoveRedEyeIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </InputAdornment>
        }
      />

      <LoadingButton
        size="xlarge"
        onClick={handleSubmit(handleNextStepOnClick)}
        disabled={!isDirty || isSubmitting}
        loading={isSubmitting}
        variant="contained"
        fullWidth
      >
        {t('layout.auth.buttons.continue', 'Continue')}
      </LoadingButton>

      <RHFCheckbox
        label={t(
          'layout.auth.steps.setupPassword.form.subscribe',
          'Keep me in loop with Knowz',
        )}
        description={t(
          'layout.auth.steps.setupPassword.form.subscribeDetails',
          'By checking this you agree to receive occasional marketing related communications from Knowz',
        )}
        name="is_subscribe_marketing"
      />
    </BaseStep>
  );
}
